function stripQuotes(name: string): string {
  return name.replace(/^["']|["']$/g, '').trim();
}

function basenameFromDispositionValue(name: string): string {
  const n = stripQuotes(name);
  if (/[a-zA-Z]:\\/.test(n) || n.includes('\\')) {
    const normalized = n.replace(/\\/g, '/');
    const last = normalized.split('/').pop();
    return last && last.length > 0 ? last : n;
  }
  return n;
}

function parseFilenameFromContentDisposition(
  header: string | null,
): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;\n]+)/i.exec(header);
  if (star) {
    try {
      return basenameFromDispositionValue(
        decodeURIComponent(star[1].trim().replace(/["']$/g, '')),
      );
    } catch {
      return null;
    }
  }
  const plain = /filename\s*=\s*("?)([^";\n]+)\1/i.exec(header);
  if (plain) return basenameFromDispositionValue(plain[2]);
  return null;
}

function withExtension(name: string, formatKey: string): string {
  const trimmed = name.trim();
  if (/\.(pdf|xlsx|zip)$/i.test(trimmed)) {
    return trimmed;
  }
  const ext =
    formatKey === 'pdf' ? '.pdf' : formatKey === 'xlsx' ? '.xlsx' : '';
  if (!ext) return trimmed || `qr-codes.${formatKey}`;
  const lower = trimmed.toLowerCase();
  if (lower.endsWith(ext)) return trimmed;
  const base = trimmed.replace(/\.[^./\\]+$/, '');
  return `${base || 'qr-codes'}${ext}`;
}

function triggerDownload(url: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function resolveDownloadFileName(
  response: Response,
  formatKey: string,
): string {
  const fromCd = parseFilenameFromContentDisposition(
    response.headers.get('content-disposition'),
  );
  return withExtension(fromCd || `qr-codes.${formatKey}`, formatKey);
}

export async function openPrintResponse(
  response: Response,
  formatKey: string,
  printWindow: Window | null,
): Promise<void> {
  const blob = await response.blob();
  const fileName = resolveDownloadFileName(response, formatKey);
  const lowerName = fileName.toLowerCase();
  let mime =
    blob.type && blob.type !== 'application/octet-stream' ? blob.type : '';
  if (!mime) {
    if (lowerName.endsWith('.zip')) mime = 'application/zip';
    else if (lowerName.endsWith('.pdf')) mime = 'application/pdf';
    else if (lowerName.endsWith('.xlsx'))
      mime =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    else
      mime =
        formatKey === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }

  const namedFile = new File([blob], fileName, { type: mime });
  const url = URL.createObjectURL(namedFile);

  triggerDownload(url, fileName);
  printWindow?.close();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
